/**
 * InlineNoteBar — compact note entry bar for detail pages.
 *
 * Shows on group detail pages where the account is already known.
 * No GroupSearch needed — account is passed as props.
 */

import { useState, useCallback } from 'react';
import {
  makeStyles,
  Select,
  Button,
  Textarea,
  Spinner,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import { COLORS } from '@shared/ui';
import { useCreateNote } from '@shared/entities/annotation';
import type { NoteType } from '@shared/entities/annotation/model/types';
import { NoteTagButton } from './NoteTagButton';
import { useNoteConfig } from './useNoteConfig';

// =============================================================================
// Styles
// =============================================================================

const useStyles = makeStyles({
  bar: {
    backgroundColor: COLORS.bgSurface,
    border: `1px solid ${COLORS.border}`,
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '16px',
  },
  row: {
    display: 'flex',
    gap: '10px',
    alignItems: 'flex-start',
  },
  fields: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  topRow: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  typeSelect: {
    minWidth: '160px',
  },
  bottomRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  hint: {
    fontSize: '11px',
    color: COLORS.textMuted,
  },
});

// =============================================================================
// Component
// =============================================================================

interface InlineNoteBarProps {
  accountId: string;
  accountName: string;
}

export function InlineNoteBar({ accountId, accountName }: InlineNoteBarProps) {
  const styles = useStyles();
  const createNote = useCreateNote();
  const { noteTypes, noteTags } = useNoteConfig();

  const [noteType, setNoteType] = useState<string>('General Note');
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [content, setContent] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleToggleTag = useCallback((tag: string) => {
    setSelectedTags(prev => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  }, []);

  const handlePost = useCallback(async () => {
    if (!content.trim()) return;
    setError('');
    try {
      await createNote.mutateAsync({
        accountId,
        noteType: noteType as NoteType,
        tags: [...selectedTags] as any,
        content: content.trim(),
      });
      setSuccess('Note posted');
      setContent('');
      setSelectedTags(new Set());
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to post note');
    }
  }, [accountId, noteType, selectedTags, content, createNote]);

  return (
    <div className={styles.bar}>
      <div className={styles.row}>
        <div className={styles.fields}>
          <div className={styles.topRow}>
            <Select
              className={styles.typeSelect}
              size="small"
              value={noteType}
              onChange={(_, d) => setNoteType(d.value)}
            >
              {noteTypes.map((t) => (
                <option key={t.value} value={t.value}>{t.value}</option>
              ))}
            </Select>
            {noteTags.map((tag) => (
              <NoteTagButton
                key={tag.value}
                tag={tag}
                selected={selectedTags.has(tag.value)}
                onToggle={handleToggleTag}
              />
            ))}
          </div>
          <Textarea
            value={content}
            onChange={(_, d) => setContent(d.value)}
            placeholder={`Add a note about ${accountName}...`}
            resize="vertical"
            size="small"
            style={{ minHeight: '44px' }}
          />
          {error && (
            <MessageBar intent="error">
              <MessageBarBody>{error}</MessageBarBody>
            </MessageBar>
          )}
          {success && (
            <MessageBar intent="success">
              <MessageBarBody>{success}</MessageBarBody>
            </MessageBar>
          )}
          <div className={styles.bottomRow}>
            <span className={styles.hint}>Posts to Dynamics CRM timeline</span>
            <Button
              appearance="primary"
              size="small"
              onClick={handlePost}
              disabled={!content.trim() || createNote.isPending}
              icon={createNote.isPending ? <Spinner size="tiny" /> : undefined}
            >
              {createNote.isPending ? 'Posting...' : 'Post Note'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
